// Admin /admin/compliance "Today" tab — operational view that turns Phase
// 5 + 6 data into actionable signals: which children are blocked from
// drop-off (informational), and which classrooms are at staffing/cert
// risk over the next 7 days.

import { useCallback, useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import {
  ShieldCheck, Warning, ArrowsClockwise, ArrowRight, Lightning,
} from "@phosphor-icons/react";
import { toast } from "sonner";
import {
  fetchClearanceSummary,
  fetchRatioCompliance,
  groupRatioByClassroom,
} from "@/lib/dailyOpsCompliance";
import { markAttendanceOverride } from "@/lib/attendance";
import { supabase } from "@/lib/supabase";
import { useAuth } from "@/context/AuthContext";
import { listHealthConfigurationGaps } from "@/lib/healthOperations";

const DAY_HEADERS = (() => {
  const out = [];
  for (let i = 0; i < 7; i++) {
    const d = new Date();
    d.setDate(d.getDate() + i);
    out.push({
      key: d.toISOString().slice(0, 10),
      label: d.toLocaleDateString(undefined, { weekday: "short" }),
      sub: d.toLocaleDateString(undefined, { month: "numeric", day: "numeric" }),
      isToday: i === 0,
    });
  }
  return out;
})();

export default function AdminComplianceTodayTab() {
  const { profile } = useAuth();
  const organizationId = profile?.organization_id;
  const [clearance, setClearance] = useState([]);
  const [ratios, setRatios] = useState([]);
  const [configurationGaps, setConfigurationGaps] = useState([]);
  const [enforcement, setEnforcement] = useState(false);
  const [loading, setLoading] = useState(true);
  const [overrideRow, setOverrideRow] = useState(null); // { childId, childName, blockerSummary }
  const [overrideReason, setOverrideReason] = useState("");
  const [overrideBusy, setOverrideBusy] = useState(false);

  const refresh = useCallback(async () => {
    setLoading(true);
    try {
      const [c, r, g, orgRow] = await Promise.all([
        fetchClearanceSummary(),
        fetchRatioCompliance(),
        listHealthConfigurationGaps(),
        organizationId
          ? supabase
              .from("organizations")
              .select("enforce_attendance_clearance")
              .eq("id", organizationId)
              .maybeSingle()
          : Promise.resolve({ data: null }),
      ]);
      setClearance(c || []);
      setRatios(r || []);
      setConfigurationGaps(g || []);
      setEnforcement(!!orgRow?.data?.enforce_attendance_clearance);
    } catch (err) {
      toast.error(err.message || "Could not load today's compliance");
    } finally {
      setLoading(false);
    }
  }, [organizationId]);

  useEffect(() => { refresh(); }, [refresh]);

  const ratioByClass = useMemo(() => groupRatioByClassroom(ratios), [ratios]);
  const blockedToday = clearance.length;
  const ratioBreachClassrooms = useMemo(() => {
    const set = new Set();
    for (const r of ratios) if (r.ratioBreach) set.add(r.classroomId);
    return set.size;
  }, [ratios]);
  const certBreachClassrooms = useMemo(() => {
    const set = new Set();
    for (const r of ratios) if (r.certBreach) set.add(r.classroomId);
    return set.size;
  }, [ratios]);

  return (
    <div data-testid="admin-compliance-today" className="space-y-6">
      <div className="flex items-center justify-end">
        <button
          type="button"
          data-testid="admin-compliance-today-refresh"
          onClick={refresh}
          className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-neutral-700 hover:bg-neutral-50"
        >
          <ArrowsClockwise size={12} weight="bold" /> Refresh
        </button>
      </div>

      <section className="grid grid-cols-2 gap-3 sm:grid-cols-4">
        <Kpi testId="admin-compliance-today-kpi-blocked" label="Blocked at drop-off" value={blockedToday} tone={blockedToday > 0 ? "alert" : "ok"} />
        <Kpi testId="admin-compliance-today-kpi-ratio" label="Ratio risk this week" value={ratioBreachClassrooms} tone={ratioBreachClassrooms > 0 ? "warn" : "ok"} />
        <Kpi testId="admin-compliance-today-kpi-cert" label="Cert risk this week" value={certBreachClassrooms} tone={certBreachClassrooms > 0 ? "warn" : "ok"} />
        <Kpi testId="admin-compliance-today-kpi-configuration" label="Health setup needed" value={configurationGaps.length} tone={configurationGaps.length > 0 ? "warn" : "ok"} />
      </section>

      <section data-testid="admin-compliance-today-configuration" className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex flex-wrap items-center gap-3">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-amber-50 text-amber-800">
            <Warning size={16} weight="duotone" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Requirement configuration</p>
            <p className="text-sm font-semibold text-neutral-900">Finish health requirement setup before asking families for action</p>
          </div>
          <div className="flex flex-wrap gap-2">
            <Link to="/admin/compliance?tab=catalog" className="rounded-full border border-primary/40 bg-primary/5 px-3 py-1.5 text-[11px] font-semibold text-primary-hover hover:bg-primary/10">Review catalog</Link>
            <Link to="/admin/classrooms" className="rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-[11px] font-semibold text-neutral-700 hover:bg-neutral-50">Review classrooms</Link>
          </div>
        </div>
        {loading && <p className="mt-3 text-sm text-neutral-500">Loading…</p>}
        {!loading && configurationGaps.length === 0 && (
          <p className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">All scoped health requirements can be evaluated for enrolled children.</p>
        )}
        {configurationGaps.length > 0 && (
          <ul className="mt-3 space-y-2">
            {configurationGaps.map((gap) => (
              <li key={`${gap.childId}-${gap.recordTypeId}`} data-testid={`admin-compliance-configuration-${gap.childId}-${gap.recordTypeId}`} className="rounded-2xl border border-amber-300 bg-amber-50/70 p-3">
                <p className="text-sm font-semibold text-neutral-900">{gap.recordTypeLabel} · {gap.childName}</p>
                <p className="mt-0.5 text-[11px] text-neutral-600">{gap.classroomName ? `${gap.classroomName} · ` : ""}{gap.reason}</p>
              </li>
            ))}
          </ul>
        )}
        <p className="mt-3 text-[11px] italic text-neutral-500">These are center setup tasks, not family compliance failures and not attendance blocks.</p>
      </section>

      {/* Children blocked at drop-off */}
      <section data-testid="admin-compliance-today-blocked" className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-[#C6851E]/10 text-[#8A5A12]">
            <Warning size={16} weight="duotone" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Drop-off attention</p>
            <p className="text-sm font-semibold text-neutral-900">Children who would be flagged at drop-off today</p>
          </div>
        </div>
        {loading && <p className="mt-3 text-sm text-neutral-500">Loading…</p>}
        {!loading && clearance.length === 0 && (
          <p data-testid="admin-compliance-today-blocked-empty" className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">
            All children are cleared based on the records on file.
          </p>
        )}
        <ul className="mt-3 space-y-2">
          {clearance.map((c) => (
            <li
              key={c.childId}
              data-testid={`admin-compliance-today-row-${c.childId}`}
              className="flex flex-wrap items-center gap-3 rounded-2xl border border-[#C6851E]/30 bg-[#C6851E]/5 p-3"
            >
              <div className="min-w-0 flex-1">
                <p className="truncate text-sm font-semibold text-neutral-900">{c.childName}</p>
                <p className="truncate text-[11px] text-neutral-500">
                  {c.classroomName ? `${c.classroomName} · ` : ""}
                  {c.blockerCount} {c.blockerCount === 1 ? "blocker" : "blockers"}: {c.blockerSummary}
                </p>
              </div>
              <span className="rounded-full border border-[#C6851E]/40 bg-white px-2 py-0.5 text-[10px] font-bold uppercase tracking-wider text-[#8A5A12]">
                {enforcement ? "Blocks check-in" : "Action needed"}
              </span>
              {enforcement && (
                <button
                  type="button"
                  data-testid={`admin-compliance-today-override-${c.childId}`}
                  onClick={() => {
                    setOverrideRow({
                      childId: c.childId,
                      childName: c.childName,
                      blockerSummary: c.blockerSummary,
                    });
                    setOverrideReason("");
                  }}
                  className="inline-flex items-center gap-1 rounded-full border border-primary/40 bg-white px-2.5 py-1 text-[11px] font-semibold text-primary-hover hover:bg-primary/5"
                >
                  <Lightning size={10} weight="duotone" />
                  Override
                </button>
              )}
            </li>
          ))}
        </ul>
        <p className="mt-3 flex items-center gap-1.5 text-[11px] italic text-neutral-500">
          {enforcement ? (
            <>
              <ShieldCheck size={12} weight="duotone" className="text-primary-hover" />
              <span>
                Enforcement <strong className="text-primary-hover">ON</strong> — teachers cannot mark these children present without an admin override. Toggle in Settings.
              </span>
            </>
          ) : (
            <span>Informational only — attendance check-in is not blocked. Toggle enforcement in Settings.</span>
          )}
        </p>
      </section>

      {/* Classroom ratio + cert compliance heatmap */}
      <section data-testid="admin-compliance-today-ratios" className="rounded-2xl border border-neutral-200 bg-white p-4">
        <div className="flex items-center gap-2">
          <span className="flex h-8 w-8 items-center justify-center rounded-xl bg-primary/10 text-primary-hover">
            <ShieldCheck size={16} weight="duotone" />
          </span>
          <div className="min-w-0 flex-1">
            <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">Staffing & ratio compliance</p>
            <p className="text-sm font-semibold text-neutral-900">Today + the next 6 days</p>
          </div>
        </div>
        {loading && <p className="mt-3 text-sm text-neutral-500">Loading…</p>}
        {!loading && ratioByClass.length === 0 && (
          <p data-testid="admin-compliance-today-ratios-empty" className="mt-3 rounded-2xl border border-dashed border-neutral-200 bg-neutral-50/30 px-3 py-4 text-center text-sm text-neutral-500">
            No classrooms configured yet.
          </p>
        )}
        {ratioByClass.length > 0 && (
          <div className="mt-3 overflow-x-auto">
            <table className="w-full min-w-[520px] text-[12px]">
              <thead>
                <tr className="text-left text-[10px] font-bold uppercase tracking-wider text-neutral-500">
                  <th className="px-2 py-1.5">Classroom</th>
                  {DAY_HEADERS.map((d) => (
                    <th key={d.key} className={`px-1.5 py-1.5 text-center ${d.isToday ? "text-primary-hover" : ""}`}>
                      <div>{d.label}</div>
                      <div className="text-[9px] font-semibold text-neutral-400">{d.sub}</div>
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {ratioByClass.map((cls) => (
                  <tr key={cls.classroomId} data-testid={`admin-compliance-today-ratio-row-${cls.classroomId}`} className="border-t border-neutral-100">
                    <td className="px-2 py-2 align-top">
                      <p className="font-semibold text-neutral-900">{cls.classroomName}</p>
                      <p className="text-[10px] text-neutral-500">
                        {cls.requiredRatio ? `1 : ${cls.requiredRatio}` : "no ratio set"}
                      </p>
                    </td>
                    {cls.days.map((d) => {
                      const tone = d.ratioBreach
                        ? "bg-[#C6851E]/10 text-[#8A5A12] border-[#C6851E]/40"
                        : d.certBreach
                          ? "bg-amber-50 text-amber-900 border-amber-300"
                          : "bg-emerald-50 text-emerald-800 border-emerald-200";
                      const title = `${d.headCount} children · ${d.caregiversCertified}/${d.caregiversCount} certified${d.ratioBreach ? " · over ratio" : ""}${d.certBreach ? " · cert gap" : ""}`;
                      return (
                        <td key={d.day} className="px-1 py-1.5 text-center">
                          <span
                            data-testid={`admin-compliance-today-cell-${cls.classroomId}-${d.day}`}
                            data-ratio-breach={d.ratioBreach ? "true" : "false"}
                            data-cert-breach={d.certBreach ? "true" : "false"}
                            title={title}
                            className={`mx-auto inline-flex min-w-[38px] flex-col items-center rounded-lg border px-1 py-0.5 ${tone}`}
                          >
                            <span className="text-[10px] font-bold">{d.headCount} / {d.caregiversCount}</span>
                            {d.certBreach && (
                              <span className="text-[8px] font-bold uppercase tracking-wider">cert gap</span>
                            )}
                            {d.ratioBreach && (
                              <span className="text-[8px] font-bold uppercase tracking-wider">over ratio</span>
                            )}
                          </span>
                        </td>
                      );
                    })}
                  </tr>
                ))}
              </tbody>
            </table>
            <p className="mt-2 text-[11px] italic text-neutral-500">
              Children-per-caregiver ratio uses each classroom's <strong>required ratio</strong> (Settings → Classrooms). Cert gap = at least one assigned caregiver is missing a required cert that day.
            </p>
          </div>
        )}
      </section>
      {overrideRow && (
        <OverrideDialog
          row={overrideRow}
          reason={overrideReason}
          busy={overrideBusy}
          onReason={setOverrideReason}
          onClose={() => { setOverrideRow(null); setOverrideReason(""); }}
          onConfirm={async () => {
            if (overrideReason.trim().length < 3) {
              toast.error("Please enter a reason (at least 3 characters).");
              return;
            }
            setOverrideBusy(true);
            try {
              await markAttendanceOverride({
                childId: overrideRow.childId,
                status: "present",
                reason: overrideReason.trim(),
              });
              toast.success(`${overrideRow.childName} marked present · override logged.`);
              setOverrideRow(null);
              setOverrideReason("");
              await refresh();
            } catch (err) {
              toast.error(err.message || "Override failed");
            } finally {
              setOverrideBusy(false);
            }
          }}
        />
      )}
    </div>
  );
}

function OverrideDialog({ row, reason, busy, onReason, onClose, onConfirm }) {
  return (
    <div
      data-testid="admin-compliance-override-dialog"
      className="fixed inset-0 z-50 flex items-end justify-center bg-black/40 p-4 sm:items-center"
      onClick={(e) => { if (e.target === e.currentTarget && !busy) onClose(); }}
    >
      <div
        role="dialog"
        aria-modal="true"
        aria-labelledby="override-title"
        className="w-full max-w-md overflow-hidden rounded-2xl bg-white shadow-2xl"
      >
        <header className="bg-primary/5 px-5 pt-5 pb-4">
          <p id="override-title" className="font-heading text-lg font-semibold tracking-tight text-neutral-900">
            Override attendance block
          </p>
          <p className="mt-0.5 text-sm text-neutral-600">
            Mark <span className="font-semibold text-neutral-900">{row.childName}</span> present today despite the compliance gap.
          </p>
        </header>
        <div className="space-y-4 px-5 py-5 text-sm">
          <div>
            <p className="text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">Blockers</p>
            <p className="mt-1 rounded-xl bg-neutral-50 px-3 py-2 text-[13px] font-semibold text-neutral-900">
              {row.blockerSummary}
            </p>
          </div>
          <label className="block">
            <span className="text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              Reason (logged, required)
            </span>
            <textarea
              data-testid="admin-compliance-override-reason"
              value={reason}
              onChange={(e) => onReason(e.target.value)}
              disabled={busy}
              rows={3}
              placeholder="e.g. Record on file, uploading to portal today."
              className="mt-1 w-full rounded-xl border border-neutral-200 bg-white px-3 py-2 text-[13px] focus:border-primary focus:outline-none focus:ring-2 focus:ring-primary/20"
            />
          </label>
          <div className="flex flex-col gap-2">
            <button
              type="button"
              data-testid="admin-compliance-override-confirm"
              onClick={onConfirm}
              disabled={busy || reason.trim().length < 3}
              className="rounded-full bg-primary px-4 py-2.5 text-sm font-semibold text-white hover:bg-primary-hover disabled:opacity-60"
            >
              {busy ? "Overriding…" : "Mark present anyway"}
            </button>
            <button
              type="button"
              data-testid="admin-compliance-override-cancel"
              onClick={onClose}
              disabled={busy}
              className="rounded-full border border-neutral-200 bg-white px-4 py-2.5 text-sm font-semibold text-neutral-700 hover:bg-neutral-50"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Kpi({ testId, label, value, tone }) {
  const cls = {
    ok:    "border-emerald-200",
    warn:  "border-amber-300",
    alert: "border-[#C6851E]/40",
  }[tone || ""] || "border-neutral-200";
  return (
    <div data-testid={testId} className={`rounded-2xl border bg-white p-4 ${cls}`}>
      <p className="text-[11px] font-bold uppercase tracking-wider text-neutral-500">{label}</p>
      <p className="mt-1 font-heading text-2xl font-semibold text-neutral-900">{value}</p>
    </div>
  );
}
